// TemplatesPage — grilla de templates del workspace (+ globales/variants), con
// filtros, preview hero (TemplateThumbnail), acciones por-card (toggle/duplicate/
// delete/customize), edición inline de colorMode/suitableFor y modal de creación.
// Styled-components en ./templatesPage/styled; el preview en components/templatesPage.
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { TemplateSummary, CANVAS_PRESETS, CanvasPreset } from '@agency-smm/shared';
import { apiClient } from '../services/apiClient';
import { pickPreviewPhoto, PreviewPhotoCandidate } from '../utils/previewPhoto';
import { useAuth } from '../hooks/useAuth';
import { Button } from '../components/Button';
import { CardTitle } from '../components/Card';
import { Input, InputWrapper, Label } from '../components/Input';
import { TemplateThumbnail } from '../components/templatesPage/TemplateThumbnail';
import {
  PageHeader, FilterBar, FilterSearchInput, FilterSelect, FilterCount, FilterReset,
  Title, Subtitle, TemplatesGrid, TemplateCard, TemplateInfo, DefaultBadge, GlobalBadge,
  EmptyState, Modal, ModalContent, ModalTitle, ModalActions, PresetSelect, DeleteButton,
  DuplicateButton, ToggleButton, InactiveOverlay, DisabledBadge, ColorModeBadge,
  ColorModeSelect, SourceToggles, SourceChip, CustomizeBtn, CardWrapper, ClampedDescription,
} from './templatesPage/styled';

const SOURCE_CHIP_COLORS: Record<string, string> = {
  stock: '#3B82F6',
  pastel: '#A855F7',
  ai: '#F59E0B',
  asset: '#10B981',
};

const SOURCE_LABELS: Record<string, string> = {
  stock: 'Stock',
  pastel: 'Color',
  ai: 'AI',
  asset: 'Custom',
};

const ALL_SOURCES = ['stock', 'pastel', 'ai', 'asset'];

type StatusFilter = 'all' | 'active' | 'disabled';
type SourceFilter = 'all' | 'mine' | 'global' | 'variant';
type FormatFilter = 'all' | 'square' | 'vertical' | 'wide';

function classifyFormat(w: number, h: number): FormatFilter {
  if (h > w * 1.1) return 'vertical';
  if (w > h * 1.1) return 'wide';
  return 'square';
}

export function TemplatesPage() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // Workspace logo URL — fed into preview thumbnails so `workspace_logo`
  // image elements render the real brand mark instead of a placeholder.
  const [workspaceLogoUrl, setWorkspaceLogoUrl] = useState<string | undefined>(undefined);
  const [brandPhoneNumber, setBrandPhoneNumber] = useState<string | undefined>(undefined);
  // Paleta de la marca + una foto propia: la preview deja de ser un ejemplo
  // genérico y muestra el template con los datos reales del workspace.
  const [brandColors, setBrandColors] = useState<string[] | undefined>(undefined);
  const [brandFonts, setBrandFonts] = useState<string[] | undefined>(undefined);
  // Fotos candidatas del workspace. NO se usa "la primera": muchas son ads ya
  // terminados y darían un anuncio dentro de otro. pickPreviewPhoto filtra y
  // reparte (ver utils/previewPhoto.ts).
  const [previewPhotos, setPreviewPhotos] = useState<PreviewPhotoCandidate[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [selectedPreset, setSelectedPreset] = useState<CanvasPreset>('instagram_square');
  const [isCreating, setIsCreating] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const [isGlobalNew, setIsGlobalNew] = useState(false);

  // Filter state — narrow the grid quickly without reloading the list.
  const [filterQuery, setFilterQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<StatusFilter>('all');
  const [filterSource, setFilterSource] = useState<SourceFilter>('all');
  const [filterFormat, setFilterFormat] = useState<FormatFilter>('all');

  const visibleTemplates = templates.filter((t) => {
    if (filterStatus === 'active' && t.isActive === false) return false;
    if (filterStatus === 'disabled' && t.isActive !== false) return false;
    if (filterSource === 'global' && !t.isGlobal) return false;
    if (filterSource === 'mine' && t.isGlobal) return false;
    if (filterSource === 'variant' && !t.parentTemplateId) return false;
    if (filterFormat !== 'all' && classifyFormat(t.canvasWidth, t.canvasHeight) !== filterFormat) return false;
    if (filterQuery.trim()) {
      const q = filterQuery.toLowerCase();
      const haystack = `${t.name || ''} ${t.description || ''}`.toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });
  const filtersActive = filterQuery.trim().length > 0 || filterStatus !== 'all' || filterSource !== 'all' || filterFormat !== 'all';
  const resetFilters = () => {
    setFilterQuery('');
    setFilterStatus('all');
    setFilterSource('all');
    setFilterFormat('all');
  };

  useEffect(() => {
    if (workspaceId) {
      fetchTemplates();
      fetchWorkspaceLogo();
      fetchBrandPhone();
    }
  }, [workspaceId]);

  const fetchWorkspaceLogo = async () => {
    // Skip when previewing global templates from the admin __global__ context
    // (no real workspace = no logo to fetch).
    if (!workspaceId || workspaceId === '__global__') return;
    try {
      const logo = await apiClient.get<{ url: string } | null>(`/api/workspaces/${workspaceId}/logo`);
      if (logo?.url) setWorkspaceLogoUrl(logo.url);
    } catch {
      // No logo set on this workspace — fall back to placeholder.
    }
  };

  const fetchBrandPhone = async () => {
    if (!workspaceId || workspaceId === '__global__') return;
    try {
      const info = await apiClient.get<{
        phoneNumber?: string; brandColors?: string[]; brandFonts?: string[];
      }>(`/api/workspaces/${workspaceId}/brand-info`);
      if (info?.phoneNumber) setBrandPhoneNumber(info.phoneNumber);
      if (info?.brandColors?.length) setBrandColors(info.brandColors);
      if (info?.brandFonts?.length) setBrandFonts(info.brandFonts);
    } catch {
      // No brand info — thumbnails con brand_phone slots quedan ocultos.
    }
    try {
      const imgs = await apiClient.get<PreviewPhotoCandidate[]>(`/api/workspaces/${workspaceId}/background-images`);
      if (Array.isArray(imgs)) setPreviewPhotos(imgs);
    } catch { /* sin fotos, pickPreviewPhoto cae al stock */ }
  };

  const fetchTemplates = async () => {
    try {
      setIsLoading(true);
      const data = await apiClient.get<TemplateSummary[]>(`/api/workspaces/${workspaceId}/templates`);
      setTemplates(data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateTemplate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTemplateName.trim()) return;

    setIsCreating(true);
    try {
      const preset = CANVAS_PRESETS[selectedPreset];
      const template = await apiClient.post<TemplateSummary>(`/api/workspaces/${workspaceId}/templates`, {
        name: newTemplateName,
        canvasWidth: preset.width,
        canvasHeight: preset.height,
        isGlobal: isGlobalNew,
      });
      setTemplates([template, ...templates]);
      setIsModalOpen(false);
      setNewTemplateName('');
      setIsGlobalNew(false);
      // Navigate to editor
      navigate(`/workspace/${workspaceId}/templates/${template.id}`);
    } catch (error) {
      console.error('Failed to create template:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteTemplate = async (e: React.MouseEvent, template: TemplateSummary) => {
    e.stopPropagation();
    if (template.isGlobal && !user?.isAdmin) return;
    if (!confirm('Are you sure you want to delete this template?')) return;

    try {
      await apiClient.delete(`/api/workspaces/${workspaceId}/templates/${template.id}`);
      setTemplates(templates.filter((t) => t.id !== template.id));
    } catch (error) {
      console.error('Failed to delete template:', error);
    }
  };

  const handleDuplicateTemplate = async (e: React.MouseEvent, templateId: string) => {
    e.stopPropagation();
    setDuplicatingId(templateId);

    try {
      const duplicated = await apiClient.post<TemplateSummary>(
        `/api/workspaces/${workspaceId}/templates/${templateId}/duplicate`,
        {}
      );
      setTemplates([duplicated, ...templates]);
    } catch (error) {
      console.error('Failed to duplicate template:', error);
    } finally {
      setDuplicatingId(null);
    }
  };

  const handleToggleActive = async (e: React.MouseEvent, templateId: string) => {
    e.stopPropagation();
    // Optimistic toggle in local state — no full re-fetch (avoids the visible
    // page flash). Server is the source of truth on next mount; we revert on error.
    const flip = (list: TemplateSummary[]) =>
      list.map((t) =>
        t.id === templateId ? { ...t, isActive: t.isActive === false } : t
      );
    setTemplates(flip);
    try {
      await apiClient.patch(`/api/workspaces/${workspaceId}/templates/${templateId}/toggle-active`, {});
    } catch (err) {
      console.error('Failed to toggle template:', err);
      setTemplates(flip);
    }
  };

  if (isLoading) {
    return <div>Loading templates...</div>;
  }

  return (
    <>
      <PageHeader>
        <div>
          <Title>Templates</Title>
          <Subtitle>Create overlay templates to brand your generated content</Subtitle>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <Button $variant="outline" onClick={() => navigate(`/workspace/${workspaceId}`)}>
            Back to Workspace
          </Button>
          <Button onClick={() => setIsModalOpen(true)}>+ New Template</Button>
        </div>
      </PageHeader>

      {templates.length === 0 ? (
        <EmptyState>
          <p>No templates yet. Create your first template to add brand overlays to your posts!</p>
          <Button onClick={() => setIsModalOpen(true)} style={{ marginTop: 16 }}>
            Create Template
          </Button>
        </EmptyState>
      ) : (
        <>
          <FilterBar>
            <FilterSearchInput
              type="text"
              placeholder="Search by name or description…"
              value={filterQuery}
              onChange={(e) => setFilterQuery(e.target.value)}
            />
            <FilterSelect value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as StatusFilter)}>
              <option value="all">All status</option>
              <option value="active">● Active</option>
              <option value="disabled">○ Disabled</option>
            </FilterSelect>
            <FilterSelect value={filterSource} onChange={(e) => setFilterSource(e.target.value as SourceFilter)}>
              <option value="all">All sources</option>
              <option value="mine">My templates</option>
              <option value="global">Global</option>
              <option value="variant">Workspace variants</option>
            </FilterSelect>
            <FilterSelect value={filterFormat} onChange={(e) => setFilterFormat(e.target.value as FormatFilter)}>
              <option value="all">All formats</option>
              <option value="square">Square (1:1)</option>
              <option value="vertical">Vertical (9:16)</option>
              <option value="wide">Wide (16:9)</option>
            </FilterSelect>
            <FilterCount>
              {visibleTemplates.length === templates.length
                ? `${templates.length} templates`
                : `${visibleTemplates.length} of ${templates.length}`}
            </FilterCount>
            {filtersActive && <FilterReset onClick={resetFilters}>Clear</FilterReset>}
          </FilterBar>

          {visibleTemplates.length === 0 ? (
            <EmptyState>
              <p>No templates match these filters.</p>
              <Button $variant="outline" onClick={resetFilters} style={{ marginTop: 12 }}>Clear filters</Button>
            </EmptyState>
          ) : (
            <TemplatesGrid>
              {visibleTemplates.map((template) => {
            const isInactive = template.isActive === false;
            return (
              <CardWrapper key={template.id} $inactive={isInactive}>
                {isInactive && <DisabledBadge>Disabled</DisabledBadge>}
                <TemplateCard onClick={() => navigate(`/workspace/${workspaceId}/templates/${template.id}`)}>
                  <ToggleButton
                    onClick={(e) => handleToggleActive(e, template.id)}
                    title={isInactive ? 'Enable template' : 'Disable template'}
                  >
                    {isInactive ? '○' : '●'}
                  </ToggleButton>
                  <DuplicateButton
                    onClick={(e) => handleDuplicateTemplate(e, template.id)}
                    disabled={duplicatingId === template.id}
                    title="Duplicate template"
                  >
                    {duplicatingId === template.id ? '...' : '⧉'}
                  </DuplicateButton>
                  {(!template.isGlobal || user?.isAdmin) && (
                    <DeleteButton onClick={(e) => handleDeleteTemplate(e, template)} title="Delete template">×</DeleteButton>
                  )}
                  {isInactive && <InactiveOverlay />}
                  <TemplateThumbnail
                    templateId={template.id}
                    workspaceId={workspaceId!}
                    canvasWidth={template.canvasWidth}
                    canvasHeight={template.canvasHeight}
                    workspaceLogoUrl={workspaceLogoUrl}
                    brandPhoneNumber={brandPhoneNumber}
                    sampleImageUrl={pickPreviewPhoto(previewPhotos, template.id)}
                    brandColors={brandColors}
                    brandFonts={brandFonts}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <CardTitle style={{ fontSize: '15px', fontWeight: 700, lineHeight: 1.2 }}>{template.name}</CardTitle>
                      {template.description && (
                        <ClampedDescription>{template.description}</ClampedDescription>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                      {template.isGlobal && <GlobalBadge>Global</GlobalBadge>}
                      {template.isDefault && <DefaultBadge>Default</DefaultBadge>}
                      {template.colorMode && template.colorMode !== 'fixed' && (
                        <ColorModeBadge $mode={template.colorMode}>
                          {template.colorMode === 'brand' ? 'Brand' : 'Adaptive'}
                        </ColorModeBadge>
                      )}
                    </div>
                  </div>
                  <TemplateInfo>
                    <span>{template.elementCount} elements · {template.canvasWidth}×{template.canvasHeight}</span>
                  </TemplateInfo>
                  <ColorModeSelect
                    value={template.colorMode || 'fixed'}
                    onClick={(e) => e.stopPropagation()}
                    onChange={async (e) => {
                      e.stopPropagation();
                      try {
                        await apiClient.put(`/api/workspaces/${workspaceId}/templates/${template.id}`, {
                          colorMode: e.target.value,
                        });
                        fetchTemplates();
                      } catch { /* ignore */ }
                    }}
                  >
                    <option value="fixed">Fixed Colors</option>
                    <option value="brand">Brand Colors</option>
                    <option value="adaptive">Adaptive</option>
                  </ColorModeSelect>
                  <SourceToggles onClick={(e) => e.stopPropagation()}>
                    {ALL_SOURCES.map((source) => {
                      const suitable = template.suitableFor || 'all';
                      const activeSources = suitable === 'all' ? ALL_SOURCES : suitable.split(',').map(s => s.trim());
                      const isActive = activeSources.includes(source);
                      return (
                        <SourceChip
                          key={source}
                          $active={isActive}
                          $color={SOURCE_CHIP_COLORS[source]}
                          onClick={async () => {
                            let newSources: string[];
                            if (isActive) {
                              newSources = activeSources.filter(s => s !== source);
                              if (newSources.length === 0) return; // at least one must be active
                            } else {
                              newSources = [...activeSources, source];
                            }
                            const newValue = newSources.length === ALL_SOURCES.length ? 'all' : newSources.join(',');
                            try {
                              await apiClient.put(`/api/workspaces/${workspaceId}/templates/${template.id}`, {
                                suitableFor: newValue,
                              });
                              fetchTemplates();
                            } catch { /* ignore */ }
                          }}
                        >
                          {SOURCE_LABELS[source]}
                        </SourceChip>
                      );
                    })}
                  </SourceToggles>
                  {template.isGlobal && (
                    <CustomizeBtn
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          const duplicated = await apiClient.post<{ id: string }>(
                            `/api/workspaces/${workspaceId}/templates/${template.id}/duplicate`,
                            {}
                          );
                          navigate(`/workspace/${workspaceId}/templates/${duplicated.id}`);
                        } catch (err) {
                          console.error('Failed to duplicate:', err);
                        }
                      }}
                    >
                      Customize Copy
                    </CustomizeBtn>
                  )}
                </TemplateCard>
              </CardWrapper>
            );
          })}
            </TemplatesGrid>
          )}
        </>
      )}

      {isModalOpen && (
        <Modal onClick={() => setIsModalOpen(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Create New Template</ModalTitle>
            <form onSubmit={handleCreateTemplate}>
              <InputWrapper style={{ marginBottom: 16 }}>
                <Label htmlFor="templateName">Template Name</Label>
                <Input
                  id="templateName"
                  type="text"
                  value={newTemplateName}
                  onChange={(e) => setNewTemplateName(e.target.value)}
                  placeholder="e.g., Logo Watermark"
                  required
                  autoFocus
                />
              </InputWrapper>
              <InputWrapper>
                <Label htmlFor="canvasPreset">Canvas Size</Label>
                <PresetSelect
                  id="canvasPreset"
                  value={selectedPreset}
                  onChange={(e) => setSelectedPreset(e.target.value as CanvasPreset)}
                >
                  {Object.entries(CANVAS_PRESETS).map(([key, preset]) => (
                    <option key={key} value={key}>
                      {preset.label} ({preset.width}×{preset.height})
                    </option>
                  ))}
                </PresetSelect>
              </InputWrapper>
              {user?.isAdmin && (
                <InputWrapper style={{ marginTop: 16 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={isGlobalNew}
                      onChange={(e) => setIsGlobalNew(e.target.checked)}
                    />
                    <span>Create as Global Template (visible to all users)</span>
                  </label>
                </InputWrapper>
              )}
              <ModalActions>
                <Button type="button" $variant="outline" onClick={() => setIsModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isCreating || !newTemplateName.trim()}>
                  {isCreating ? 'Creating...' : 'Create & Edit'}
                </Button>
              </ModalActions>
            </form>
          </ModalContent>
        </Modal>
      )}
    </>
  );
}
